/**
 * Cookie utility functions for managing browser cookies
 */
export interface CookieOptions {
  domain?: string;
  path?: string;
  expires?: Date | string;
  maxAge?: number;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  httpOnly?: boolean;
}

/**
 * Set a cookie with optional domain, path, and expiration
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Cookie options
 */
export const setCookie = (name: string, value: string, options: CookieOptions = {}): void => {
  const { domain, path = '/', expires, maxAge, secure, sameSite, httpOnly } = options;

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  if (path) cookieString += `; path=${path}`;
  if (domain) cookieString += `; domain=${domain}`;
  if (expires) cookieString += `; expires=${expires}`;
  if (maxAge !== undefined) cookieString += `; max-age=${maxAge}`;
  if (secure) cookieString += `; secure`;
  if (sameSite) cookieString += `; samesite=${sameSite}`;
  if (httpOnly) cookieString += `; httponly`;

  document.cookie = cookieString;
};

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
  const cookies = document.cookie.split(';');

  const matchingCookie = cookies.find((cookie) => {
    const trimmedCookie = cookie.trim();
    const eqIndex = trimmedCookie.indexOf('=');

    if (eqIndex === -1) {
      // Handle cookies without equals sign (just the name)
      return decodeURIComponent(trimmedCookie) === name;
    }

    const cookieName = trimmedCookie.substring(0, eqIndex);
    return decodeURIComponent(cookieName) === name;
  });

  if (!matchingCookie) {
    return undefined;
  }

  const trimmedCookie = matchingCookie.trim();
  const eqIndex = trimmedCookie.indexOf('=');

  if (eqIndex === -1) {
    // Cookie without equals sign (just the name)
    return '';
  }

  const cookieValue = trimmedCookie.substring(eqIndex + 1);
  return decodeURIComponent(cookieValue || '');
};

/**
 * Delete a cookie by setting its expiration date to the past
 * @param name - Cookie name
 * @param options - Cookie options (domain and path)
 */
export const deleteCookie = (
  name: string,
  options: Pick<CookieOptions, 'domain' | 'path'> = {},
): void => {
  const { domain, path = '/' } = options;

  let cookieString = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;

  if (path) cookieString += `; path=${path}`;
  if (domain) cookieString += `; domain=${domain}`;

  document.cookie = cookieString;
};

/**
 * Delete cookies by name across multiple domains and paths
 * @param cookieName - Name of the cookie to delete
 * @param domains - Array of domains to clear from. If not provided, uses default domains
 * @param paths - Array of paths to clear from. Defaults to ['/']
 */
export const deleteCookieAcrossDomains = (
  cookieName: string,
  domains?: string[],
  paths: string[] = ['/'],
): void => {
  const defaultDomains = ['', window.location.hostname, `.${window.location.hostname}`];

  const domainsToUse = domains && domains.length > 0 ? domains : defaultDomains;

  domainsToUse.forEach((domain) => {
    paths.forEach((path) => {
      deleteCookie(cookieName, { domain: domain || undefined, path });
    });
  });
};

/**
 * Get all cookies as an object
 * @returns Object with cookie names as keys and values as values
 */
export const getAllCookies = (): Record<string, string> => {
  const cookies: Record<string, string> = {};

  document.cookie.split(';').forEach((cookie) => {
    const trimmedCookie = cookie.trim();
    const eqIndex = trimmedCookie.indexOf('=');

    if (eqIndex === -1) {
      // Handle cookies without equals sign (just the name)
      if (trimmedCookie) {
        cookies[decodeURIComponent(trimmedCookie)] = '';
      }
      return;
    }

    const name = trimmedCookie.substring(0, eqIndex);
    const value = trimmedCookie.substring(eqIndex + 1);

    if (name) {
      cookies[decodeURIComponent(name)] = decodeURIComponent(value);
    }
  });

  return cookies;
};

/**
 * Clear all cookies that match a pattern
 * @param pattern - RegExp pattern to match cookie names
 * @param domains - Optional domains to clear from
 * @param paths - Optional paths to clear from
 */
export const clearCookiesByPattern = (
  pattern: RegExp,
  domains?: string[],
  paths?: string[],
): void => {
  const allCookies = getAllCookies();

  Object.keys(allCookies).forEach((cookieName) => {
    if (pattern.test(cookieName)) {
      deleteCookieAcrossDomains(cookieName, domains, paths);
    }
  });
};
